import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';

// Import slices (we'll create these next)
import siteSlice from './slices/siteSlice';
import uiSlice from './slices/uiSlice';
import errorSlice from './slices/errorSlice';
import settingsSlice from './slices/settingsSlice';
import agGridSlice from './slices/agGridSlice';
import authSlice from './slices/authSlice';

// Import API endpoints (we'll create this next)
import { apiEndpoints } from '@/services/api';
import { adminApi } from '@/services/adminApi';
import { apiErrorMiddleware } from '@/utils/apiErrorHandler';

// Combine reducers
const rootReducer = combineReducers({
  site: siteSlice,
  ui: uiSlice,
  error: errorSlice,
  settings: settingsSlice,
  agGrid: agGridSlice,
  auth: authSlice,
  [apiEndpoints.reducerPath]: apiEndpoints.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
});

// Persist configuration
const persistConfig = {
  key: 'wsapi-admin',
  version: 1,
  storage,
  whitelist: ['site', 'settings', 'ui'], // Only persist these slices
  blacklist: [apiEndpoints.reducerPath, adminApi.reducerPath], // Don't persist API cache
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Error logging middleware
const errorLoggingMiddleware = () => (next: any) => (action: any) => {
  try {
    return next(action);
  } catch (error) {
    console.error('Redux middleware error:', error);
    throw error;
  }
};

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(apiEndpoints.middleware)
      .concat(adminApi.middleware)
      .concat(apiErrorMiddleware)
      .concat(errorLoggingMiddleware),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

// Types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;